/**
 * All date maths. Two rules hold everything together:
 *
 *  1. A calendar day is a `YYYY-MM-DD` string. Subscriptions end on a *day*,
 *     not at an instant, so storing timestamps would let a browser in a
 *     different timezone decide a membership expired a day early.
 *
 *  2. The PC clock is not trusted. Front desk machines drift, and staff
 *     occasionally change the date. `now()` returns server time as last
 *     observed during sync, offset by elapsed local time.
 */
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
  endOfMonth,
} from 'date-fns';
import type { ISODate, ISOTimestamp } from './types';

/* -------------------------------------------------------------------------
   Trusted clock
   ------------------------------------------------------------------------- */

/**
 * Milliseconds to add to the local clock to get server time.
 * Set on every successful sync and persisted in settings; 0 until then, which
 * simply means "trust the local clock for now".
 */
let clockOffsetMs = 0;

export function setClockOffsetMs(offset: number): void {
  clockOffsetMs = Number.isFinite(offset) ? offset : 0;
}

export function getClockOffsetMs(): number {
  return clockOffsetMs;
}

/**
 * Compute the offset from a server timestamp.
 *
 * `requestSentAt` lets us subtract network latency: the server's clock is
 * sampled somewhere between sending the request and receiving the reply, so we
 * assume the midpoint. Without this, a slow connection reads as clock drift.
 */
export function computeClockOffsetMs(
  serverTime: Date | string,
  requestSentAt: number,
  responseReceivedAt: number
): number {
  const server = typeof serverTime === 'string' ? parseISO(serverTime) : serverTime;
  if (!isValid(server)) return 0;
  const roundTrip = responseReceivedAt - requestSentAt;
  const localMidpoint = requestSentAt + roundTrip / 2;
  return server.getTime() - localMidpoint;
}

/** The current instant, corrected for clock drift. Use this, never `new Date()`. */
export function now(): Date {
  return new Date(Date.now() + clockOffsetMs);
}

/** Today as `YYYY-MM-DD`, corrected for clock drift. */
export function todayISO(): ISODate {
  return toISODate(now());
}

/** The current instant as an ISO-8601 UTC string, for audit fields. */
export function nowTimestamp(): ISOTimestamp {
  return now().toISOString();
}

/* -------------------------------------------------------------------------
   Conversion
   ------------------------------------------------------------------------- */

/** Format a Date as a local calendar day. Never use .toISOString() for this —
 *  that converts to UTC and can shift the day backwards for +02:00 Cairo. */
export function toISODate(date: Date): ISODate {
  return format(date, 'yyyy-MM-dd');
}

/** Parse `YYYY-MM-DD` to a Date at LOCAL midnight. */
export function fromISODate(iso: ISODate): Date {
  return parseISO(iso);
}

export function isValidISODate(iso: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) && isValid(parseISO(iso));
}

/** Human display: `12 Sep 2026`. */
export function formatDisplayDate(iso: ISODate): string {
  if (!isValidISODate(iso)) return '—';
  return format(parseISO(iso), 'd MMM yyyy');
}

/** Longer display for headers: `Sunday, 2 August 2026`. */
export function formatLongDate(iso: ISODate): string {
  if (!isValidISODate(iso)) return '—';
  return format(parseISO(iso), 'EEEE, d MMMM yyyy');
}

/**
 * Local wall-clock time from an audit timestamp, `HH:mm`.
 *
 * `visited_at` is a calendar day with no time of day (see the type doc on
 * WalkIn), so "what time did they come in" can only come from `created_at` —
 * an instant, correctly converted to whatever timezone the device is in.
 */
export function formatTime(iso: ISOTimestamp): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, 'HH:mm') : '—';
}

/* -------------------------------------------------------------------------
   Subscription date maths
   ------------------------------------------------------------------------- */

/**
 * The end date of a subscription.
 *
 * date-fns clamps month-end correctly: 31 Jan + 1 month = 28 Feb (29 in a leap
 * year), not 3 March. That clamping is the entire reason we use date-fns here
 * rather than hand-rolling with setMonth.
 */
export function computeEndDate(startDate: ISODate, months: number): ISODate {
  return toISODate(addMonths(fromISODate(startDate), months));
}

/**
 * Days from `today` until `endDate`. Positive = still running, 0 = last day,
 * negative = expired. Calendar days, so partial days never round oddly.
 */
export function daysRemaining(endDate: ISODate, today: ISODate): number {
  return differenceInCalendarDays(fromISODate(endDate), fromISODate(today));
}

/**
 * Where a renewal should start.
 *
 * Still active  -> the day after the current end date, so no days are lost and
 *                  the member gets exactly what they paid for.
 * Already expired -> today, since backdating would sell days already gone.
 *
 * The front desk can always override the returned value.
 */
export function renewalStartDate(currentEndDate: ISODate, today: ISODate): ISODate {
  const remaining = daysRemaining(currentEndDate, today);
  if (remaining >= 0) {
    return toISODate(addDays(fromISODate(currentEndDate), 1));
  }
  return today;
}

/** Inclusive `YYYY-MM-DD` bounds of the month containing `iso`. */
export function monthBounds(iso: ISODate): { from: ISODate; to: ISODate } {
  const d = fromISODate(iso);
  return { from: toISODate(startOfMonth(d)), to: toISODate(endOfMonth(d)) };
}

/** Inclusive `YYYY-MM-DD` bounds of the Mon-Sun week containing `iso`. */
export function weekBounds(iso: ISODate): { from: ISODate; to: ISODate } {
  const d = fromISODate(iso);
  return {
    from: toISODate(startOfWeek(d, { weekStartsOn: 1 })),
    to: toISODate(endOfWeek(d, { weekStartsOn: 1 })),
  };
}

/** Inclusive string comparison — safe because ISO dates sort lexicographically. */
export function isWithin(date: ISODate, from: ISODate, to: ISODate): boolean {
  return date >= from && date <= to;
}

/** Shift a calendar day by N months, e.g. for the portal's 12-month trend. */
export function shiftMonths(iso: ISODate, months: number): ISODate {
  return toISODate(addMonths(fromISODate(iso), months));
}

/** Shift a calendar day by N days. */
export function shiftDays(iso: ISODate, days: number): ISODate {
  return toISODate(addDays(fromISODate(iso), days));
}

/** `2026-08` — the bucket key for monthly revenue grouping. */
export function monthKey(iso: ISODate): string {
  return iso.slice(0, 7);
}

/** `Aug 2026` — display label for a month bucket. */
export function formatMonthKey(key: string): string {
  const d = parseISO(`${key}-01`);
  return isValid(d) ? format(d, 'MMM yyyy') : key;
}
